
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import EditUserNameInput from "./EditUserNameInput";
import EditUserPhoneInput from "./EditUserPhoneInput";
import EditUserRoleSelect from "./EditUserRoleSelect";
import EditUserCountrySelect from "./EditUserCountrySelect";
import EditUserConstituencySelect from "./EditUserConstituencySelect";
import EditUserVerifiedCheckbox from "./EditUserVerifiedCheckbox";
import EditUserDemographicsFields from "./EditUserDemographicsFields";
import { FormData } from "./types";
import { DemographicsFormValues } from "@/components/profile/demographics/types";

export type UserProfile = Database['public']['Tables']['profiles']['Row'];
export type UserRole = Database['public']['Enums']['user_role'];

export interface Country {
  code: string;
  name: string;
}

export interface Constituency {
  code: string;
  name: string;
  country_code: string;
}

interface EditUserFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedUser: UserProfile | null;
  onUpdateUser: () => Promise<void>;
  formData: FormData;
  setFormData: (data: FormData) => void;
}

const EditUserForm = ({
  open,
  setOpen,
  selectedUser,
  onUpdateUser,
  formData,
  setFormData
}: EditUserFormProps) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: countriesData, error: countriesError } = await supabase
          .from("countries")
          .select("code, name")
          .eq("active", true)
          .order("name");

        if (countriesError) throw countriesError;
        setCountries(countriesData || []);

        if (formData.country_code) {
          const { data: constituenciesData, error: constituenciesError } = await supabase
            .from("constituencies")
            .select("code, name, country_code")
            .eq("country_code", formData.country_code)
            .eq("active", true)
            .order("name");

          if (constituenciesError) throw constituenciesError;
          setConstituencies(constituenciesData || []);
        } else {
          setConstituencies([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [open, formData.country_code]);

  const handleCountryChange = (value: string) => {
    setFormData({
      ...formData,
      country_code: value,
      constituency_code: ""
    });
  };

  const handleDemographicsChange = (values: DemographicsFormValues) => {
    setFormData({
      ...formData,
      demographics: values
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto p-0 flex flex-col w-full max-w-lg rounded-lg bg-background"
        style={{ maxHeight: '90vh' }}
      >
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2 bg-background z-10 sticky top-0">
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user role and information. User: {selectedUser?.name}
          </DialogDescription>
        </DialogHeader>

        <div
          className="grow px-6 py-4 overflow-y-auto space-y-6"
          style={{ minHeight: 0, maxHeight: '60vh' }}
        >
          <div className="space-y-2">
            <EditUserNameInput
              name={formData.name}
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className="space-y-2">
            <EditUserPhoneInput
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className="space-y-2">
            <EditUserRoleSelect
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className="space-y-2">
            <EditUserCountrySelect
              countries={countries}
              formData={formData}
              onCountryChange={(val) => {
                setFormData({
                  ...formData,
                  country_code: val,
                  constituency_code: ""
                });
              }}
            />
          </div>

          <div className="space-y-2">
            <EditUserConstituencySelect
              constituencies={constituencies}
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className="pt-4 space-y-2 border-t border-gray-200">
            <EditUserVerifiedCheckbox
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className="pt-6 border-t border-gray-200">
            <EditUserDemographicsFields
              demographics={formData.demographics}
              setDemographics={(vals) => {
                setFormData({
                  ...formData,
                  demographics: vals
                });
              }}
            />
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 bg-background z-10 sticky bottom-0 space-x-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={onUpdateUser} disabled={loading}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserForm;
